import { GlobalEvents, GlobalState } from '@highlight/ipc'
import AudioNoteIpcRenderer from '@renderer/ipc/AudioNoteIpc.renderer'
import type { AudioNoteState } from '@shared/types/AudioNoteState'
import type { TrackedParticipant } from '@shared/types/meeting-participant'
import { compareCodeUnits } from '@renderer/utils/string'
import {
  getMeetingParticipants,
  subscribeToMeetingParticipants,
} from '@renderer/services/meeting-participants-consumer'
import {
  SPEAKER_RESOLVER_EVIDENCE_UPDATE_EVENT,
  type SpeakerEvidenceEntry,
  type SpeakerResolverEvidenceSnapshot,
} from '@shared/speaker-resolver-evidence'

/**
 * Correlates Deepgram speaker indices with meeting participant names detected
 * via macOS Accessibility APIs.
 *
 * Two resolution strategies:
 *
 * 1. **Process of elimination** (1:1 calls): If there's only one non-self
 *    participant, all non-host Deepgram indices map to them immediately.
 *    No speaking detection needed. Invalidated when participants change.
 *
 * 2. **Evidence accumulation** (group calls): Each time a Deepgram segment
 *    arrives, all currently-speaking AX participants get a "vote" for that
 *    index. The name with the most votes wins once a confidence threshold
 *    is reached. Multiple indices can resolve to the same name (handles
 *    Deepgram splitting one speaker into multiple indices).
 */

const MIN_CONFIDENCE_VOTES = 1
const LOG_PREFIX = '[SpeakerNameResolver]'

/**
 * Maximum Deepgram indices *within a single Deepgram session* that can
 * resolve to the same participant name via evidence accumulation. When
 * exceeded, the offending name's resolutions in that session are dropped —
 * this indicates a shared microphone where Deepgram correctly diarizes
 * distinct voices but AX attributes all speech to one participant (e.g. a
 * conference room mic).
 *
 * The "single Deepgram session" scoping is critical: across Deepgram
 * reconnects (signalled by the audio-sessions DO via
 * `speaker-namespace-bumped`) the *same* participant legitimately gets a
 * fresh dgSpeakerIndex, and counting those toward the threshold would
 * mis-classify a normal multi-session participant as a shared mic.
 *
 * Matches the spirit of MERGED_AUDIO_MIN_DIARIZED_SPEAKERS (3) in the
 * Recall path's merged-audio hog detection.
 */
const MAX_DG_INDICES_PER_NAME = 2

type DgSource = 'sys' | 'mic'

export class SpeakerNameResolver {
  /** votes[deepgramIndex][participantName] = count */
  private votes = new Map<string, Map<string, number>>()

  constructor() {
    GlobalState.on('speaker-manual-overrides', () => {
      this.publishState()
    })
    subscribeToMeetingParticipants(() => {
      this.publishState()
    })
  }

  /** Cached resolution — recomputed when votes change */
  private resolved = new Map<string, string>()

  /** Indices that were resolved via process-of-elimination */
  private eliminationIndices = new Set<string>()

  /** Sorted comma-joined participant names when elimination was last applied */
  private eliminationParticipantKey = ''

  /** All DG indices ever passed to resolve(), so they appear in evidence state even without votes */
  private seenIndices = new Set<string>()

  /**
   * Participants already flagged `isSpeaking=true` at the moment the resolver
   * was reset. Their speaking state is suspected stale carry-over from a
   * prior session (e.g. AX service was torn down mid-speech so a debounce
   * timer never flipped it off). We refuse to vote for these participants
   * until we observe their state change — an `isSpeaking=false` transition
   * proves the upstream tracker is live and any future `isSpeaking=true` is
   * legitimate. Keyed by participant id.
   */
  private suspectedStaleSpeakingIds = new Set<number>()

  /**
   * Last `resolvedNames` map we forwarded to the audio session DO. Diffed
   * against each new publication so we only POST changed entries instead of
   * the full map every recompute. Keyed by full prefixed dgSpeakerIndex
   * (e.g. `"sys:0"`, `"mic:0"`).
   */
  private lastForwardedResolvedNames = new Map<string, string>()

  /**
   * Per-source list of dgSpeakerIndex offsets at which each Deepgram-session
   * epoch begins. The first epoch is implicit (boundary 0); subsequent
   * boundaries are appended when the audio-sessions DO emits a
   * `speaker-namespace-bumped` status. `epochFor()` resolves a bare
   * dgSpeakerIndex value to the epoch it belongs to. The shared-mic
   * detection in `recompute` then groups indices by (name, epoch) so
   * cross-session re-issuances of the same participant don't trip the
   * threshold.
   */
  private epochBoundariesBySource = new Map<DgSource, Array<number>>()

  resolve(speaker: string): string {
    // Non-numeric speakers pass through (e.g., "You", "Other")
    if (!/^\d+$/.test(speaker)) return speaker

    this.seenIndices.add(speaker)

    // Manual overrides use full prefixed keys (e.g., "sys:0"); resolve() receives bare indices
    const manualOverrides = GlobalState.get<Record<string, string>>('speaker-manual-overrides', {})
    if (manualOverrides[`sys:${speaker}`]) return manualOverrides[`sys:${speaker}`]

    const participants = getMeetingParticipants()

    // Let suspected-stale flags decay as soon as the upstream tracker proves
    // it's live (state transitions to false, or the participant disappears).
    this.refreshStaleSpeakingSuspicions(participants)

    // Invalidate elimination caches if participant situation changed
    this.invalidateEliminationIfNeeded(participants)

    // Always record AX evidence — even for previously cached entries —
    // so evidence accumulates continuously and can override stale resolutions
    this.recordEvidence(speaker, participants)

    // Check resolved cache (evidence-based or still-valid elimination)
    const cached = this.resolved.get(speaker)
    if (cached) return cached

    // Try process-of-elimination for 1:1 calls
    const eliminated = this.tryElimination(speaker, participants)
    if (eliminated) return eliminated

    // Fallback: 1-indexed label
    return `Speaker ${parseInt(speaker, 10) + 1}`
  }

  /**
   * Record a Deepgram-session boundary as signalled by the audio-sessions DO
   * (`{ type: 'status', message: 'speaker-namespace-bumped', source, offset }`).
   * Indices `>= offset` belong to a new epoch; earlier indices stay assigned
   * to the prior one. Idempotent on duplicates and out-of-order signals
   * (defensive — the DO sends each boundary once, but cross-process IPC
   * could in principle deliver a duplicate).
   */
  notifyNamespaceBumped(source: DgSource, offset: number): void {
    if (!Number.isFinite(offset) || offset < 0) return
    let boundaries = this.epochBoundariesBySource.get(source)
    if (!boundaries) {
      boundaries = [0]
      this.epochBoundariesBySource.set(source, boundaries)
    }
    if (offset > boundaries[boundaries.length - 1]) {
      boundaries.push(offset)
    }
  }

  /**
   * Map a bare dgSpeakerIndex value (e.g. `5`, parsed out of `"sys:5"`) to
   * its Deepgram-session epoch. Epoch 0 is the first session; each
   * `speaker-namespace-bumped` boundary increments the epoch number for
   * indices `>= offset`. Returns 0 when no boundaries have been recorded.
   */
  epochFor(source: DgSource, bareIdx: number): number {
    const boundaries = this.epochBoundariesBySource.get(source)
    if (!boundaries || boundaries.length <= 1) return 0
    let epoch = 0
    for (let i = 1; i < boundaries.length; i++) {
      if (bareIdx >= boundaries[i]) epoch = i
      else break
    }
    return epoch
  }

  /**
   * When participants join or leave, elimination-based resolutions may be wrong.
   * Clear them so evidence accumulation takes over.
   */
  private invalidateEliminationIfNeeded(participants: Record<number, TrackedParticipant>): void {
    if (this.eliminationIndices.size === 0) return

    const uniqueNames = this.getNonSelfParticipantNames(participants)
    const currentKey = Array.from(uniqueNames).sort(compareCodeUnits).join(',')

    if (currentKey !== this.eliminationParticipantKey) {
      console.log(
        `${LOG_PREFIX} Participants changed ("${this.eliminationParticipantKey}" → "${currentKey}"), clearing elimination cache`,
      )
      for (const idx of this.eliminationIndices) {
        this.resolved.delete(idx)
      }
      this.eliminationIndices.clear()
      this.eliminationParticipantKey = ''
    }
  }

  /**
   * If there's exactly one non-self participant, all non-host Deepgram indices
   * must be them. Resolve immediately without needing AX speaking detection.
   */
  private tryElimination(dgIndex: string, participants: Record<number, TrackedParticipant>): string | null {
    const names = this.getNonSelfParticipantNames(participants)

    // Only one non-self participant visible — must be the other person in a 1:1
    if (names.size === 1) {
      const name = names.values().next().value!
      this.resolved.set(dgIndex, name)
      this.eliminationIndices.add(dgIndex)
      this.eliminationParticipantKey = Array.from(names).sort(compareCodeUnits).join(',')
      console.log(`${LOG_PREFIX} Index ${dgIndex} → ${name} (only participant)`)
      this.publishState()
      return name
    }

    return null
  }

  /**
   * Drop suspected-stale flags for any participant whose state has since
   * advanced: either they stopped speaking (the upstream tracker is alive
   * and responsive) or they disappeared from the participant list entirely.
   * After this, future `isSpeaking=true` observations for them count as
   * legitimate votes.
   */
  private refreshStaleSpeakingSuspicions(participants: Record<number, TrackedParticipant>): void {
    if (this.suspectedStaleSpeakingIds.size === 0) return
    for (const id of Array.from(this.suspectedStaleSpeakingIds)) {
      const p = participants[id]
      if (!p || !p.isSpeaking) {
        this.suspectedStaleSpeakingIds.delete(id)
      }
    }
  }

  private getNonSelfParticipantNames(participants: Record<number, TrackedParticipant>): Set<string> {
    return new Set(
      Object.values(participants)
        .filter((p) => p.name && !p.isHost && !p.isSelf)
        .map((p) => p.name!),
    )
  }

  private recordEvidence(dgIndex: string, participants: Record<number, TrackedParticipant>): void {
    const speaking = Object.values(participants).filter(
      (p) => p.isSpeaking && p.name && !p.isSelf && !this.suspectedStaleSpeakingIds.has(p.id),
    )

    if (speaking.length === 0) return

    let votesForIndex = this.votes.get(dgIndex)
    if (!votesForIndex) {
      votesForIndex = new Map<string, number>()
      this.votes.set(dgIndex, votesForIndex)
    }

    for (const p of speaking) {
      const name = p.name!
      const prev = votesForIndex.get(name) ?? 0
      votesForIndex.set(name, prev + 1)
    }

    this.recompute()
  }

  /**
   * Recompute all index→name assignments using a greedy best-match approach.
   * Multiple Deepgram indices CAN resolve to the same participant name.
   */
  private recompute(): void {
    const newResolved = new Map<string, string>()

    // Preserve non-evidence-based resolutions (e.g., valid elimination entries)
    for (const [idx, name] of this.resolved) {
      if (!this.votes.has(idx)) {
        newResolved.set(idx, name)
      }
    }

    for (const [dgIndex, nameVotes] of this.votes) {
      let bestName: string | null = null
      let bestCount = 0

      for (const [name, count] of nameVotes) {
        if (count > bestCount) {
          bestCount = count
          bestName = name
        }
      }

      if (bestName && bestCount >= MIN_CONFIDENCE_VOTES) {
        if (this.resolved.get(dgIndex) !== bestName) {
          console.log(
            `${LOG_PREFIX} Index ${dgIndex} → ${bestName} (${bestCount} votes, ${nameVotes.size} candidate(s))`,
          )
        }
        newResolved.set(dgIndex, bestName)
      }
    }

    // Shared-mic detection: within a single Deepgram session, if too many
    // distinct dgSpeakerIndices resolve to the same participant name,
    // Deepgram is correctly separating voices that AX attributes to one
    // person. Drop those resolutions so each voice keeps its own "Speaker
    // N" label and the user can rename them by ear.
    //
    // Grouping by epoch is what makes this safe across Deepgram reconnects:
    // the *same* participant legitimately gets a fresh dgSpeakerIndex on
    // every fresh dial, so counting indices across epochs would mis-fire
    // on ordinary multi-session participants. Epoch boundaries come from
    // the DO via `notifyNamespaceBumped`.
    const indicesByNameAndEpoch = new Map<string, Map<number, Array<string>>>()
    for (const [dgIndex, name] of newResolved) {
      if (this.eliminationIndices.has(dgIndex)) continue
      const bareIdx = parseInt(dgIndex, 10)
      if (!Number.isFinite(bareIdx)) continue
      const epoch = this.epochFor('sys', bareIdx)
      let perEpoch = indicesByNameAndEpoch.get(name)
      if (!perEpoch) {
        perEpoch = new Map()
        indicesByNameAndEpoch.set(name, perEpoch)
      }
      let indices = perEpoch.get(epoch)
      if (!indices) {
        indices = []
        perEpoch.set(epoch, indices)
      }
      indices.push(dgIndex)
    }

    for (const [name, perEpoch] of indicesByNameAndEpoch) {
      for (const [epoch, indices] of perEpoch) {
        if (indices.length > MAX_DG_INDICES_PER_NAME) {
          console.log(
            `${LOG_PREFIX} Shared mic detected (epoch=${epoch}): ${indices.length} Deepgram indices → "${name}", unmapping all`,
          )
          for (const idx of indices) {
            newResolved.delete(idx)
          }
        }
      }
    }

    this.resolved = newResolved
    this.publishState()
  }

  private publishState(): void {
    const speakers: Array<SpeakerEvidenceEntry> = []
    const manualOverrides = GlobalState.get<Record<string, string>>('speaker-manual-overrides', {})

    // Collect all known bare Deepgram indices (seen, voted, resolved)
    // Manual overrides use full prefixed keys (e.g., "mic:2") so they're handled separately
    const allIndices = new Set<string>(this.seenIndices)
    for (const idx of this.votes.keys()) allIndices.add(idx)
    for (const idx of this.resolved.keys()) allIndices.add(idx)
    // Include system-speaker overrides (strip "sys:" prefix to get bare index)
    for (const key of Object.keys(manualOverrides)) {
      if (key.startsWith('sys:')) allIndices.add(key.slice(4))
    }

    for (const dgIndex of allIndices) {
      const nameVotes = this.votes.get(dgIndex)
      const votes: Record<string, number> = {}
      let totalVotes = 0
      if (nameVotes) {
        for (const [name, count] of nameVotes) {
          votes[name] = count
          totalVotes += count
        }
      }

      const isManualOverride = manualOverrides[`sys:${dgIndex}`] !== undefined

      speakers.push({
        dgIndex,
        resolvedName: isManualOverride ? manualOverrides[`sys:${dgIndex}`] : (this.resolved.get(dgIndex) ?? null),
        isElimination: this.eliminationIndices.has(dgIndex),
        votes,
        totalVotes,
        isManualOverride,
      })
    }

    // Sort by index for stable display
    speakers.sort((a, b) => parseInt(a.dgIndex, 10) - parseInt(b.dgIndex, 10))

    const participants = getMeetingParticipants()

    const state: Omit<SpeakerResolverEvidenceSnapshot, 'revision'> = {
      speakers,
      participantCount: new Set(
        Object.values(participants)
          .filter((p) => p.name)
          .map((p) => p.name!),
      ).size,
      eliminationActive: this.eliminationIndices.size > 0,
    }

    void GlobalEvents.invoke(SPEAKER_RESOLVER_EVIDENCE_UPDATE_EVENT, state).catch((error) => {
      console.error(`${LOG_PREFIX} Failed to publish evidence state`, error)
    })

    const resolvedNames: Record<string, string> = {}
    for (const [idx, name] of this.resolved) {
      resolvedNames[`sys:${idx}`] = name
    }
    // Manual overrides already use full prefixed keys (e.g., "mic:2", "sys:0")
    for (const [idx, name] of Object.entries(manualOverrides)) {
      resolvedNames[idx] = name
    }
    void GlobalState.setIfChangedInMain('speaker-resolved-names', resolvedNames).catch((error) => {
      console.error(`${LOG_PREFIX} Failed to publish resolved names`, error)
    })

    // Forward the new resolution map to the audio session DO so server-side
    // consumers (the saved transcript, staged-actions prompts, deep research)
    // see the same names the renderer paints. Without this the DO only ever
    // learns names from manual rename clicks, leaving auto-resolved names
    // stuck on raw "Speaker N" labels in storage.
    this.flushResolvedNamesToAudioSession(resolvedNames)
  }

  /**
   * Diff the latest `resolvedNames` map against what we last forwarded and
   * POST any changes (or clears) to the audio session DO. Idempotent —
   * unchanged entries don't fire a request. Best-effort: failures only log.
   *
   * Manual renames already fire their own POST from the rename popover, but
   * they also flow through here as part of `resolvedNames`. The duplicate
   * write is harmless because `applySpeakerRename` overwrites with the same
   * value.
   */
  private flushResolvedNamesToAudioSession(latest: Record<string, string>): void {
    const audioNoteState = GlobalState.get<AudioNoteState | undefined>('audio-note-state', undefined)
    const audioSessionId = audioNoteState?.audioSessionId

    if (!audioSessionId) {
      // No active audio session — bail without touching the snapshot.
      // Updating it here would mark these names as "already forwarded"
      // even though no POST went out, so once a session does arrive the
      // diff would silently drop them. Leaving the snapshot alone means
      // every name we resolved pre-session shows up as new on the first
      // post-session publish and gets forwarded then.
      return
    }

    const next = new Map(Object.entries(latest))
    const prev = this.lastForwardedResolvedNames

    for (const [dgSpeakerIndex, name] of next) {
      if (prev.get(dgSpeakerIndex) === name) continue
      AudioNoteIpcRenderer.applyAudioSessionSpeakerRename({
        audioSessionId,
        dgSpeakerIndex,
        name,
      }).catch((err) =>
        console.error(`${LOG_PREFIX} applyAudioSessionSpeakerRename failed (${dgSpeakerIndex}=${name}):`, err),
      )
    }

    for (const dgSpeakerIndex of prev.keys()) {
      if (next.has(dgSpeakerIndex)) continue
      // Resolution removed (e.g. shared-mic detection unmapped it). Send
      // null to clear the override so the DO falls back to the raw label,
      // matching what the renderer now paints.
      AudioNoteIpcRenderer.applyAudioSessionSpeakerRename({
        audioSessionId,
        dgSpeakerIndex,
        name: null,
      }).catch((err) =>
        console.error(`${LOG_PREFIX} applyAudioSessionSpeakerRename clear failed (${dgSpeakerIndex}):`, err),
      )
    }

    this.lastForwardedResolvedNames = next
  }

  reset(): void {
    this.votes.clear()
    this.resolved.clear()
    this.eliminationIndices.clear()
    this.eliminationParticipantKey = ''
    this.seenIndices.clear()
    this.epochBoundariesBySource.clear()
    // Each session gets a fresh DO with no overrides; we don't need to
    // clear remote state for the *previous* session (it's about to be
    // closed/deleted by stopAndSave) but we do need to forget what we
    // told it so the next session starts from an empty diff.
    this.lastForwardedResolvedNames.clear()

    // Snapshot anyone already marked speaking at this instant. Their flag is
    // probably a leftover from a prior session (the upstream tracker may not
    // yet have had a chance to flip it to false); treat them as suspect
    // until they prove the tracker is still live.
    this.suspectedStaleSpeakingIds.clear()
    const participants = getMeetingParticipants()
    for (const p of Object.values(participants)) {
      if (p.isSpeaking && p.name && !p.isSelf) {
        this.suspectedStaleSpeakingIds.add(p.id)
      }
    }

    GlobalState.set('speaker-manual-overrides', {})
    this.publishState()
  }
}
